import React from 'react'
import {
  AsyncSelectInput,
  SelectInput,
  AsyncDualSelect,
  ToggleInput,
  Tooltip,
} from '@demandlocal/components'
import { useGetTagCodes } from '../../../../api/utils/useGetTagCodes'
import { useGetPolkDealers } from '../../../../api/admin/useGetPolkDealers'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { motion } from 'framer-motion'
import { ReactComponent as RemoveIcon } from '../../../../images/icons/remove.svg'

export default function DealershipPremiumFeatures(): JSX.Element {
  const { control, watch, setValue, getValues } = useFormContext()
  const ttd_attribution_goals = watch('attribution_goals')
  const polk_partners = watch('polk_partners')
  const polk_competitors = watch('polk_competitors')
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'attribution_goals',
  })

  return (
    <div className="py-5 flex flex-col gap-5">
      <Tooltip
        label={'Enabling this will allow users to see the path to conversion report'}>
        <div className={'flex flex-row gap-4 items-center w-1/2'}>
          <ToggleInput
            name={'enable_path_to_conversion_report'}
            label={'Enable Access to Path to Conversion Report'}
          />
        </div>
      </Tooltip>
      <Tooltip
        label={'Enabling this will allow users to see the path to conversion user list'}>
        <div className={'flex flex-row gap-4 items-center w-1/2'}>
          <ToggleInput
            name={'enable_path_to_conversion_user_list'}
            label={'Enable Path to Conversion User List'}
          />
        </div>
      </Tooltip>
      <Tooltip label={'Enabling this will allow users to download audience data'}>
        <div className={'flex flex-row gap-4 items-center w-1/2'}>
          <ToggleInput
            name={'enable_audience_download'}
            label={'Enable Audience Downloads'}
          />
        </div>
      </Tooltip>
      <Tooltip
        label={'Enabling this will display AI insights on the monthly summary report'}>
        <div className={'flex flex-row gap-4 items-center w-1/2'}>
          <ToggleInput name={'enable_ai_insights'} label={'Enable AI Insights'} />
        </div>
      </Tooltip>
      <Tooltip
        label={
          'Enabling this will display the conversion tab in the left menu under performance'
        }>
        <div className={'flex flex-row gap-4 items-center w-1/2'}>
          <ToggleInput
            name={'enable_conversion_report'}
            label={'Enable Conversion Report'}
          />
        </div>
      </Tooltip>
      <Tooltip
        label={
          'Enabling this will display the creatives tab in the left menu under performance'
        }>
        <div className={'flex flex-row gap-4 items-center w-1/2'}>
          <ToggleInput
            name={'enable_creative_report'}
            label={'Enable Creative Performance Report'}
          />
        </div>
      </Tooltip>
      <Tooltip
        label={"Enabling this will display the polk report under the 'Performance' tab"}>
        <div className={'flex flex-row gap-4 items-center w-1/2'}>
          <ToggleInput name={'enable_polk_report'} label={'Enable Polk Report'} />
        </div>
      </Tooltip>
      <Tooltip
        label={
          "Enabling this will display the 'Path to Sale' report under the 'Performance' tab"
        }>
        <div className={'flex flex-row gap-4 items-center w-1/2'}>
          <ToggleInput
            name={'enable_path_to_sale_report'}
            label={'Enable Path to Sale Report'}
          />
        </div>
      </Tooltip>
      <Tooltip
        label={"Enabling this will display the 'GA4' report under the 'Reports' tab"}>
        <div className={'flex flex-row gap-4 items-center w-1/2'}>
          <ToggleInput name={'enable_ga4_report'} label={'Enable GA4 Report'} />
        </div>
      </Tooltip>
      <span className="text-xs text-primary-xiketic">
        <strong>Note: </strong>Other features, such as budget, RSV data, and third party
        audiences are enabled at the agency level.
      </span>
      <div className="grid grid-cols-2 gap-5 py-8 border-t-2">
        <SelectInput
          rules={{ required: ttd_attribution_goals?.length > 0 }}
          label="TTD Attribution Type"
          name={'attribution_type'}
          isClearable
          infoMessage={
            <div className="flex flex-col gap-2">
              <span>
                This will be the method of tracking conversions. Your lookback window is
                from TradeDesk, defaulted to 90 days.
              </span>
              <span className="font-semibold">
                **Setting this to first impression time attributes the conversion to the
                first campaign, otherwise it's attributed to the last campaign
              </span>
            </div>
          }
          options={[
            { label: 'Conversion Date', value: 'conversion' },
            { label: 'First Impression Date', value: 'first_impression' },
            { label: 'Last Impression Date', value: 'last_impression' },
          ]}
        />
        <div className="flex flex-col gap-2">
          <span className="font-bold text-sm">Attribution Goals</span>
          {fields.map((field, index) => {
            return (
              <div key={field.id} className="flex flex-row gap-5 items-center">
                <span className="font-bold text-sm">#{index + 1}</span>
                <AsyncSelectInput
                  name={`attribution_goals.${index}`}
                  fetchFn={useGetTagCodes}
                  labelKeyName={['tag_name', 'tag_code']}
                  customLabelFormat={'{0} [{1}]'}
                  valueKeyName={'id'}
                  placeholder={`Select attribution goal #${index + 1}`}
                />
                <button
                  className="font-bold text-md cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault()
                    remove(index)
                  }}>
                  X
                </button>
              </div>
            )
          })}
          {fields.length < 3 && (
            <button
              className={
                'self-start text-primary-persian-blue font-bold text-sm cursor-pointer'
              }
              onClick={(e) => {
                e.preventDefault()
                append({ label: '', value: '' })
              }}>
              + Add New Goal
            </button>
          )}
        </div>
      </div>
      <div className="border-t-2" />
      <div className="flex flex-col gap-2">
        <span className="font-bold text-sm">Competitor report</span>
        <span className="text-xs">
          <strong>Note:</strong> Only available for Trade Desk based advertising where
          Polk Attribution is enabled by ops.
        </span>
      </div>
      <div className={'flex flex-row'}>
        <div className={'flex flex-col pr-5'}>
          <label
            className={`block text-blue-primary text-sm font-Spartan font-bold`}
            htmlFor={'assigned_partners'}>
            Assign Partners
          </label>
          <div className={'flex flex-shrink-0'}>
            <div className={'flex flex-shrink-0 mt-4'}>
              <AsyncDualSelect
                optionsListTitle={'Choose Dealership'}
                control={control}
                selectedOptionsListTitle={'Selected Partners'}
                buttonTitle={'+ Assign Partner'}
                fetchFn={useGetPolkDealers}
                labelKeyName={'dealer_display_name'}
                valueKeyName={'polk_dealer_id'}
                searchKeyName={'dealer_display_name'}
                name={`polk_partners`}
                customOptionDetails={'dealer_address'}
              />
            </div>
          </div>
        </div>
        {polk_partners?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className={
              'pl-5 flex flex-row flex-1 flex-wrap justify-start items-end gap-3 border-l-2'
            }>
            {polk_partners?.map((value, index: number) => {
              return (
                <div
                  key={value.value}
                  className={
                    'flex flex-col p-2 bg-white border-2 min-w-32 rounded-lg min-w-[120px]'
                  }>
                  <div className={'flex justify-between text-xs'}>
                    <span className={'font-bold'}>{value.label}</span>
                    <RemoveIcon
                      className={'cursor-pointer ml-2'}
                      onClick={(e) => {
                        e.preventDefault()
                        setValue(
                          `polk_partners`,
                          getValues(`polk_partners`).filter(
                            (v) => v.value !== value.value,
                          ),
                        )
                      }}
                    />
                  </div>
                  <span className={'font-medium pt-1 text-xs'}>{value.customDetail}</span>
                </div>
              )
            })}
          </motion.div>
        )}
      </div>
      <div className={'flex flex-row'}>
        <div className={'flex flex-col pr-5'}>
          <label
            className={`block text-blue-primary text-sm font-Spartan font-bold`}
            htmlFor={'polk_competitors'}>
            Assign Competitors
          </label>
          <div className={'flex flex-shrink-0'}>
            <div className={'flex flex-shrink-0 mt-4'}>
              <AsyncDualSelect
                optionsListTitle={'Choose Dealership'}
                control={control}
                selectedOptionsListTitle={'Selected Competitors'}
                buttonTitle={'+ Assign Competitor'}
                fetchFn={useGetPolkDealers}
                labelKeyName={'dealer_display_name'}
                valueKeyName={'polk_dealer_id'}
                searchKeyName={'dealer_display_name'}
                name={`polk_competitors`}
                customOptionDetails={'dealer_address'}
              />
            </div>
          </div>
        </div>
        {polk_competitors?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className={
              'pl-5 flex flex-row flex-1 flex-wrap justify-start items-end gap-3 border-l-2'
            }>
            {polk_competitors?.map((value, index: number) => {
              return (
                <div
                  key={value.value}
                  className={
                    'flex flex-col p-2 bg-white border-2 min-w-32 rounded-lg min-w-[120px]'
                  }>
                  <div className={'flex justify-between text-xs'}>
                    <span className={'font-bold'}>{value.label}</span>
                    <RemoveIcon
                      className={'cursor-pointer ml-2'}
                      onClick={(e) => {
                        e.preventDefault()
                        setValue(
                          `polk_competitors`,
                          getValues(`polk_competitors`).filter(
                            (v) => v.value !== value.value,
                          ),
                        )
                      }}
                    />
                  </div>
                  <span className={'font-medium pt-1 text-xs'}>{value.customDetail}</span>
                </div>
              )
            })}
          </motion.div>
        )}
      </div>
    </div>
  )
}
